import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Drawer, Button, Spin, Tooltip, message, Dropdown, Modal, Select } from 'antd';
import { CloseOutlined, DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import Prescriptions from 'components/Prescriptions';
import ChatBox from 'components/ChatBox';
import s from './s.module.less';
import useProspectInfo from 'hooks/useProspectInfo';
import ProsepectInfo from 'pages/dashboard/ProspectsDetailPage/components/prosepectInfo';
import ProviderSignature from 'components/ProviderSignature';
import useLinkableVisits from 'hooks/useLinkableVisits';
import useTimezone from 'hooks/useTimezone';
import { getStandaloneNoteDetail, submitVideoTreatment, linkStandaloneNote } from 'api/treatment';
import { EBusinessType, TStandaloneNoteDetail, IVideoTreatment } from 'types/treatment';
import { TextVisitAttachment } from 'types/textVisit';
import dayjs from 'utils/dayjs';
import ProviderStore from 'store/Provider';
import { providerGetKlarityResourcesUrl } from 'api/provider';
import PrintContent from 'components/VideoVisitUnSubmittedDrawer/components/PrintContent';
import PreviewModal from 'components/VideoVisitUnSubmittedDrawer/components/PreviewModal';
import { useReactToPrint } from 'react-to-print';

const { Option } = Select;

type Props = {
    show: boolean;
    onClose: () => void;
    onSave?: () => void;
    clientId?: number;
    specialty?: string;
    noteId?: number;
    isGroupPractice?: boolean;
};

const NonAppointmentUnSubmittedVisitNote = ({ show, onClose, onSave, clientId, specialty, noteId, isGroupPractice }: Props) => {
    const [prospect, prospectLoading, fetchProspect] = useProspectInfo(clientId, isGroupPractice);
    const [isChatOpen, setIsChatOpen] = useState(true);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const prescriptionsRef = useRef<{ getValues: () => any }>(null);
    const previewContentRef = useRef<HTMLDivElement>(null);
    const [currentPlan, setCurrentPlan] = useState<IVideoTreatment>();
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const [noteDetail, setNoteDetail] = useState<TStandaloneNoteDetail | null>(null);
    const [noteLoading, setNoteLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [selectedVisitKey, setSelectedVisitKey] = useState<string | undefined>(undefined);
    const [linking, setLinking] = useState(false);
    const [visitOptions, visitLoading] = useLinkableVisits(clientId, show);
    const [, myTimezone] = useTimezone();

    const selectOptions = useMemo(() => {
        const tz = myTimezone?.value as string;
        return visitOptions.map((opt) => {
            if (opt.itemType === 'APPOINTMENT' && opt.startAt) {
                const start = dayjs(opt.startAt).tz(tz);
                const end = opt.endAt ? dayjs(opt.endAt).tz(tz) : null;
                const dateLine = start.format('dddd, MMMM D, YYYY');
                const timeLine = end
                    ? `${start.format('h:mm')} - ${end.format('h:mm a')} (${tz})${opt.duration ? ` | ${opt.duration} mins` : ''}`
                    : start.format('h:mm a');
                return {
                    value: opt.value,
                    searchText: opt.label,
                    selectedLabel: `${opt.label} - ${dateLine}`,
                    title: opt.label,
                    dateLine,
                    timeLine,
                };
            }
            if (opt.itemType === 'TEXT_VISIT') {
                const parts: string[] = [];
                if (opt.patientSubmitAt) parts.push(`Refill on ${dayjs(opt.patientSubmitAt).format('MMM D')}`);
                if (opt.patientSubmitAt) parts.push(`Submitted on ${dayjs(opt.patientSubmitAt).format('MMM D')}`);
                if (opt.updatedAt) parts.push(`Last updated on ${dayjs(opt.updatedAt).format('MMM D')}`);
                const subLine = parts.join(' | ');
                return {
                    value: opt.value,
                    searchText: opt.label,
                    selectedLabel: subLine ? `${opt.label} - ${subLine}` : opt.label,
                    title: opt.label,
                    dateLine: subLine,
                    timeLine: '',
                };
            }
            return { value: opt.value, searchText: opt.label, selectedLabel: opt.label, title: opt.label, dateLine: '', timeLine: '' };
        });
    }, [visitOptions, myTimezone]);

    const [getUser] = ProviderStore.useStore();
    const currentProviderId = getUser('data')?.id;
    const isReadOnly = !!(noteDetail && noteDetail.providerId && currentProviderId && noteDetail.providerId !== currentProviderId);
    const isSubmitted = !!noteDetail?.treatmentSubmittedAt;

    const title = specialty || 'Visit note';
    const loading = prospectLoading || noteLoading;

    const fetchNoteDetail = useCallback(async (id: number) => {
        setNoteLoading(true);
        try {
            const res = await getStandaloneNoteDetail(id);
            if (res && !res.error) {
                setNoteDetail(res.data?.data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setNoteLoading(false);
        }
    }, []);

    useEffect(() => {
        if (show && noteId) {
            fetchNoteDetail(noteId);
        }
    }, [show, noteId, fetchNoteDetail]);

    const hasVisitOptions = visitOptions.length > 0;

    const caseDataForPrescriptions = useMemo(() => {
        if (!noteDetail) return undefined;
        return {
            treatmentPlan: {
                id: noteDetail.id,
                treatmentPlan: noteDetail.treatmentPlan || '',
                providerNote: noteDetail.providerNote || '',
                aiTreatmentPlan: noteDetail.aiTreatmentPlan,
                aiTreatmentDraftStatus: noteDetail.aiTreatmentDraftStatus || false,
                autoFollowUp: noteDetail.autoFollowUp || false,
                autoFollowUpInterval: noteDetail.autoFollowUpInterval,
                autoFollowUpIntervalUnit: noteDetail.autoFollowUpIntervalUnit || 'D',
                autoFollowUpMessage: noteDetail.autoFollowUpMessage,
                attachments: noteDetail.attachments || [],
                klarityBillingNote: noteDetail.klarityBillingNote,
                diagnoses: noteDetail.diagnoses,
                billingCodes: noteDetail.billingCodes,
            },
            patientFirstName: noteDetail.patientFirstName || prospect?.firstName,
            providerId: noteDetail.providerId,
            specialty: noteDetail.specialty || specialty,
            providerSignature: noteDetail.providerSignature,
        } as any;
    }, [noteDetail, prospect, specialty]);

    const handleSubmitOrSave = async (isSave: boolean) => {
        if (!noteId || !noteDetail) return;
        try {
            setSaving(true);
            const params = prescriptionsRef.current?.getValues();
            if (!params.treatmentPlan) {
                message.error('Please add a visit notes');
                return;
            }
            const res = await submitVideoTreatment({
                ...params,
                attachments: params.attachments.map((file: TextVisitAttachment) => ({
                    name: file.name,
                    path: file.path,
                    type: file.type,
                })),
                businessType: EBusinessType.STANDALONE,
                businessId: noteId,
                submit: !isSave,
            });
            if (!res.error) {
                message.success(isSave ? 'Draft saved successfully.' : 'Visit Notes sent successfully.');
                setShowSubmitModal(false);
                onSave?.();
                if (!isSave) {
                    onClose();
                } else {
                    fetchNoteDetail(noteId);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const getSelectedVisitDate = useCallback(() => {
        if (!selectedVisitKey) return '';
        const selectedOption = visitOptions.find((opt) => opt.value === selectedVisitKey);
        if (!selectedOption) return '';
        const dateValue = selectedOption.startAt || selectedOption.createdAt;
        return dateValue ? dayjs(dateValue).format('dddd, MMMM D, YYYY') : '';
    }, [selectedVisitKey, visitOptions]);

    const handleLinkToVisit = async () => {
        if (!noteId || !selectedVisitKey) return;

        const parts = selectedVisitKey.split('_');
        const visitType = parts[0] as 'APPOINTMENT' | 'TEXT_VISIT';
        const visitId = parts.slice(1).join('_');

        setLinking(true);
        try {
            const res = await linkStandaloneNote(noteId, {
                visitType,
                visitId: visitType === 'APPOINTMENT' ? Number(visitId) : visitId,
                discardDraft: false,
            });

            if (res && !res.error) {
                if (res.data?.data === false) {
                    const visitDate = getSelectedVisitDate();
                    Modal.confirm({
                        title: 'This visit already has a draft note',
                        content: `You started a draft for this visit${visitDate ? ` on ${visitDate}` : ''}. Do you want to replace it with this note? This can't be undone.`,
                        okText: 'Replace',
                        cancelText: 'Cancel',
                        onOk: async () => {
                            try {
                                const retryRes = await linkStandaloneNote(noteId, {
                                    visitType,
                                    visitId: visitType === 'APPOINTMENT' ? Number(visitId) : visitId,
                                    discardDraft: true,
                                });
                                if (retryRes && !retryRes.error && retryRes.data?.data === true) {
                                    message.success('Visit note linked successfully.');
                                    onSave?.();
                                    onClose();
                                }
                            } catch (e) {
                                console.error(e);
                            }
                        },
                    });
                } else if (res.data?.data === true) {
                    message.success('Visit note linked successfully.');
                    onSave?.();
                    onClose();
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLinking(false);
        }
    };

    const getPreviewPlan = async (plan: IVideoTreatment) => {
        let attachmentList = plan?.attachments || [];
        if (attachmentList.length !== 0 && attachmentList.some((attachment) => attachment.previewUrl)) {
            setCurrentPlan(plan);
            setShowPreviewModal(true);
            return;
        }
        try {
            attachmentList = await Promise.all(
                attachmentList.map(async (attachment) => {
                    const res = await providerGetKlarityResourcesUrl(attachment.path || '');
                    if (!res.error) {
                        return { ...attachment, previewUrl: res.data?.data?.singedUrl || '' };
                    }
                    return attachment;
                }),
            );
            setCurrentPlan({ ...plan, attachments: attachmentList });
            setShowPreviewModal(true);
        } catch (error) {
            console.error(error);
        }
    };

    const handlePreviewAsPatient = () => {
        const plan = prescriptionsRef.current?.getValues();
        getPreviewPlan(plan);
    };

    const handlePrint = useReactToPrint({
        content: () => previewContentRef.current,
    });

    const moreMenuItems: MenuProps['items'] = [
        {
            key: 'preview',
            label: 'Preview as patient',
            onClick: handlePreviewAsPatient,
        },
        {
            key: 'print',
            label: (
                <Tooltip
                    title="Printing only includes the text of the visit notes. Attachments must be downloaded individually."
                    placement="right"
                >
                    <div className="w-full">Print</div>
                </Tooltip>
            ),
            onClick: () => {
                const plan = prescriptionsRef.current?.getValues();
                if (plan) {
                    setCurrentPlan(plan);
                    setTimeout(() => {
                        handlePrint();
                    }, 0);
                }
            },
        },
    ];

    const disableWrite = isReadOnly || isSubmitted;

    return (
        <>
            <Drawer
                open={show}
                width="99vw"
                onClose={onClose}
                placement="right"
                rootClassName={s.drawer}
                title={
                    <div className={s.header}>
                        <span className="text-base font-medium">{title}</span>
                        <Button type="text" icon={<CloseOutlined />} onClick={onClose} className={s.closeBtn} />
                    </div>
                }
                closeIcon={null}
                footer={
                    !loading && prospect && !disableWrite ? (
                        <div className={s.footer}>
                            <Button type="primary" onClick={() => setShowSubmitModal(true)}>
                                Submit
                            </Button>
                            <Dropdown
                                menu={{ items: moreMenuItems, selectable: true, defaultSelectedKeys: ['preview'] }}
                                placement="topLeft"
                                trigger={['click']}
                            >
                                <Button className={s.textBtn}>
                                    More <DownOutlined />
                                </Button>
                            </Dropdown>
                        </div>
                    ) : null
                }
                destroyOnClose
            >
                <div className={s.loadingWrapper}>
                    <Spin spinning={loading} tip="Loading...">
                        {prospect && noteDetail ? (
                            <div className={s.content}>
                                <div style={{ overflowY: 'auto', maxWidth: '30%' }}>
                                    <div className={s.previewDetail}>
                                        <ProsepectInfo
                                            hideCharge
                                            smallSize
                                            data={prospect}
                                            onUpdate={() => {
                                                fetchProspect();
                                            }}
                                            showIntakeTab
                                            showBillingForVisit
                                            isGroupPractice={isGroupPractice}
                                        />
                                    </div>
                                </div>
                                <div className={s.prescriptions}>
                                    <Prescriptions
                                        ref={prescriptionsRef}
                                        businessId={noteId!}
                                        caseData={caseDataForPrescriptions}
                                        type="standalone"
                                        hidePrescriptions
                                        visitNotesSubmitted={disableWrite}
                                    />
                                    {hasVisitOptions && !disableWrite && (
                                        <div className={s.editorSection}>
                                            <div className={s.editorSectionHeader}>
                                                <h3 className={s.editorSectionTitle}>Link to visit</h3>
                                                <p className={s.editorSectionSubtitle}>
                                                    Link this note to a visit so the claim uses the correct visit date.
                                                </p>
                                            </div>
                                            <div className={s.linkToVisitRow}>
                                                <Select
                                                    placeholder="Select a visit"
                                                    value={selectedVisitKey}
                                                    onChange={setSelectedVisitKey}
                                                    allowClear
                                                    showSearch
                                                    optionLabelProp="label"
                                                    filterOption={(input, option) =>
                                                        (option?.searchText || '').toLowerCase().includes(input.toLowerCase())
                                                    }
                                                    loading={visitLoading}
                                                    className={s.visitSelect}
                                                    listHeight={400}
                                                >
                                                    {selectOptions.map((opt) => (
                                                        <Option key={opt.value} value={opt.value} label={opt.selectedLabel} searchText={opt.searchText}>
                                                            <div>
                                                                <div>{opt.title}</div>
                                                                {opt.dateLine && <div style={{ color: '#6B7280', fontSize: 12 }}>{opt.dateLine}</div>}
                                                                {opt.timeLine && <div style={{ color: '#6B7280', fontSize: 12 }}>{opt.timeLine}</div>}
                                                            </div>
                                                        </Option>
                                                    ))}
                                                </Select>
                                                {selectedVisitKey && (
                                                    <Button
                                                        type="primary"
                                                        onClick={handleLinkToVisit}
                                                        loading={linking}
                                                    >
                                                        Link
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className={s.editorSection}>
                                        <div className={s.editorSectionHeader}>
                                            <h3 className={s.editorSectionTitle}>Upcoming visit</h3>
                                            <p className={s.editorSectionSubtitle}>
                                                The patient&apos;s upcoming visit is scheduled. Feel free to update the time anytime.
                                            </p>
                                        </div>
                                        <Button className="max-w-fit">Schedule a follow-up</Button>
                                    </div>
                                </div>
                                {isChatOpen && clientId && (
                                    <div className={s.chatBox}>
                                        <ChatBox patientId={clientId} isGroupPractice={isGroupPractice} onClose={() => setIsChatOpen(false)} />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ minHeight: 'calc(100vh - 194px)' }} />
                        )}
                    </Spin>
                </div>
            </Drawer>
            <Modal
                open={showSubmitModal}
                onCancel={() => setShowSubmitModal(false)}
                okText="Sign & Send"
                onOk={() => {
                    if (!hasSignature) {
                        message.warning('Please add your signature before submitting.');
                        return;
                    }
                    handleSubmitOrSave(false);
                }}
                width={512}
                confirmLoading={saving}
            >
                <div className="flex gap-4">
                    <div className={s.submitModalIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M8.68387 13.3419C8.88616 12.9381 9 12.4824 9 12C9 11.5176 8.88616 11.0619 8.68387 10.6581M8.68387 13.3419C8.19134 14.3251 7.17449 15 6 15C4.34315 15 3 13.6569 3 12C3 10.3431 4.34315 9 6 9C7.17449 9 8.19134 9.67492 8.68387 10.6581M8.68387 13.3419L15.3161 16.6581M8.68387 10.6581L15.3161 7.34193M15.3161 7.34193C15.8087 8.32508 16.8255 9 18 9C19.6569 9 21 7.65685 21 6C21 4.34315 19.6569 3 18 3C16.3431 3 15 4.34315 15 6C15 6.48237 15.1138 6.93815 15.3161 7.34193ZM15.3161 16.6581C15.1138 17.0619 15 17.5176 15 18C15 19.6569 16.3431 21 18 21C19.6569 21 21 19.6569 21 18C21 16.3431 19.6569 15 18 15C16.8255 15 15.8087 15.6749 15.3161 16.6581Z"
                                stroke="#0284C7"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div className="flex flex-col gap-2">
                        <div className="text-gray-900 text-lg font-medium">Submit visit notes to patient</div>
                        <div className="text-gray-500 text-sm">
                            Please review before submitting. After submission, the visit notes will be{' '}
                            <span className="font-bold">signed and sent to the patient immediately</span>. It will also
                            be recorded under your name with a timestamp.
                        </div>
                    </div>
                </div>
                <ProviderSignature onSignatureStatusChange={setHasSignature} />
            </Modal>
            {showPreviewModal && currentPlan && (
                <PreviewModal
                    open={showPreviewModal}
                    onClose={() => {
                        setShowPreviewModal(false);
                        setCurrentPlan(undefined);
                    }}
                    plan={currentPlan}
                />
            )}
            <div style={{ display: 'none' }}>
                <PrintContent ref={previewContentRef} plan={currentPlan} />
            </div>
        </>
    );
};

export default NonAppointmentUnSubmittedVisitNote;
